<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const userAgenda = useMapGetter('agenda/userAgenda');
const uiFlags = useMapGetter('agenda/uiFlags');

const DAYS = [
  { key: 'mon', label: t('AGENDA.SETTINGS.DAYS.MON') },
  { key: 'tue', label: t('AGENDA.SETTINGS.DAYS.TUE') },
  { key: 'wed', label: t('AGENDA.SETTINGS.DAYS.WED') },
  { key: 'thu', label: t('AGENDA.SETTINGS.DAYS.THU') },
  { key: 'fri', label: t('AGENDA.SETTINGS.DAYS.FRI') },
  { key: 'sat', label: t('AGENDA.SETTINGS.DAYS.SAT') },
  { key: 'sun', label: t('AGENDA.SETTINGS.DAYS.SUN') },
];

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const form = ref({
  default_meeting_name: '',
  default_duration: 60,
  add_google_meet: false,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  availability: DAYS.map(d => ({
    day: d.key,
    enabled: ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d.key),
    start_time: '09:00',
    end_time: '18:00',
  })),
});

const saved = ref(false);

function loadFromAgenda(agenda) {
  if (!agenda) return;
  form.value.default_meeting_name = agenda.default_meeting_name ?? '';
  form.value.default_duration = agenda.default_duration ?? 60;
  form.value.add_google_meet = agenda.add_google_meet ?? false;
  form.value.timezone = agenda.timezone ?? form.value.timezone;
  if (agenda.availability && Array.isArray(agenda.availability)) {
    form.value.availability = DAYS.map(d => {
      const existing = agenda.availability.find(a => a.day === d.key);
      return (
        existing ?? {
          day: d.key,
          enabled: ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d.key),
          start_time: '09:00',
          end_time: '18:00',
        }
      );
    });
  }
}

onMounted(async () => {
  if (!userAgenda.value) {
    await store.dispatch('agenda/fetchAgenda');
  }
  loadFromAgenda(userAgenda.value);
});

async function save() {
  try {
    await store.dispatch('agenda/updateAgenda', {
      default_meeting_name: form.value.default_meeting_name,
      default_duration: form.value.default_duration,
      add_google_meet: form.value.add_google_meet,
      timezone: form.value.timezone,
      availability: form.value.availability,
    });
    saved.value = true;
    setTimeout(() => {
      saved.value = false;
    }, 2500);
  } catch {
    // silently handle
  }
}
</script>

<template>
  <div
    class="fixed right-0 top-0 h-full w-full md:w-[520px] bg-n-background border-l border-n-weak shadow-2xl z-50 flex flex-col"
    @click.stop
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-5 py-4 border-b border-n-weak flex-shrink-0"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ t('AGENDA.SETTINGS.TITLE') }}
      </h2>
      <button
        class="flex items-center justify-center size-7 rounded-lg hover:bg-n-alpha-1 text-n-slate-10"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-6">
      <!-- Nome padrão -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">{{
          t('AGENDA.SETTINGS.DEFAULT_MEETING_NAME')
        }}</label>
        <input
          v-model="form.default_meeting_name"
          type="text"
          :placeholder="t('AGENDA.SETTINGS.DEFAULT_MEETING_NAME_PLACEHOLDER')"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand placeholder:text-n-slate-9"
        />
      </div>

      <!-- Duração padrão -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">{{
          t('AGENDA.SETTINGS.DEFAULT_DURATION')
        }}</label>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="dur in DURATION_OPTIONS"
            :key="dur"
            class="px-3 py-1.5 text-sm rounded border transition-colors"
            :class="
              form.default_duration === dur
                ? 'border-n-brand text-n-brand bg-n-alpha-2'
                : 'border-n-weak text-n-slate-11 hover:border-n-container'
            "
            @click="form.default_duration = dur"
          >
            {{ t(`AGENDA.MODAL.DURATION_OPTIONS.${dur}`) }}
          </button>
        </div>
      </div>

      <!-- Google Meet automático -->
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('AGENDA.SETTINGS.GOOGLE_MEET') }}
          </p>
        </div>
        <button
          class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
          :class="form.add_google_meet ? 'bg-n-brand' : 'bg-n-weak'"
          @click="form.add_google_meet = !form.add_google_meet"
        >
          <span
            class="inline-block size-3.5 transform rounded-full bg-white shadow transition-transform"
            :class="form.add_google_meet ? 'translate-x-4' : 'translate-x-1'"
          />
        </button>
      </div>

      <!-- Fuso horário -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">{{
          t('AGENDA.SETTINGS.TIMEZONE')
        }}</label>
        <input
          v-model="form.timezone"
          type="text"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
      </div>

      <!-- Disponibilidade -->
      <div class="flex flex-col gap-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('AGENDA.SETTINGS.AVAILABILITY') }}
        </p>
        <div class="flex flex-col gap-2">
          <div
            v-for="(avail, i) in form.availability"
            :key="avail.day"
            class="flex items-center gap-3"
          >
            <!-- Toggle -->
            <button
              class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0"
              :class="avail.enabled ? 'bg-n-brand' : 'bg-n-weak'"
              @click="form.availability[i].enabled = !avail.enabled"
            >
              <span
                class="inline-block size-3.5 transform rounded-full bg-white shadow transition-transform"
                :class="avail.enabled ? 'translate-x-4' : 'translate-x-1'"
              />
            </button>

            <!-- Dia -->
            <span class="text-sm text-n-slate-12 w-8 flex-shrink-0">
              {{ DAYS[i].label }}
            </span>

            <!-- Horários -->
            <template v-if="avail.enabled">
              <input
                v-model="form.availability[i].start_time"
                type="time"
                class="border border-n-weak rounded-lg px-2 py-1 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
              <span class="text-xs text-n-slate-10">{{
                t('AGENDA.SETTINGS.TO')
              }}</span>
              <input
                v-model="form.availability[i].end_time"
                type="time"
                class="border border-n-weak rounded-lg px-2 py-1 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </template>
            <span v-else class="text-sm text-n-slate-9 italic">
              {{ t('AGENDA.SETTINGS.UNAVAILABLE') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-end gap-3 px-5 py-4 border-t border-n-weak flex-shrink-0"
    >
      <span v-if="saved" class="text-sm text-n-teal-11 flex items-center gap-1">
        <span class="i-lucide-check size-4" />
        {{ t('AGENDA.SETTINGS.SAVED') }}
      </span>
      <button
        class="px-4 py-2 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 transition-all disabled:opacity-60"
        :disabled="uiFlags.isSaving"
        @click="save"
      >
        {{ t('AGENDA.SETTINGS.SAVE') }}
      </button>
    </div>
  </div>
</template>
